/**
 * Store Pages Management
 *
 * Allows store owners to manage legal pages (Privacy, Terms, etc.),
 * custom pages, and configure the footer menu.
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';

interface Store {
  id: string;
  name: string;
  slug: string;
}

interface OutletContext {
  store: Store;
}

interface StorePage {
  id: string;
  store_id: string;
  page_type: string;
  slug: string | null;
  title: string;
  content: string;
  meta_description: string | null;
  meta_keywords: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface FooterMenuItem {
  id: string;
  store_id: string;
  menu_section: string;
  label: string;
  url: string | null;
  page_id: string | null;
  link_type: string;
  store_page_type: string | null;
  position: number;
  open_new_tab: boolean;
  is_visible: boolean;
}

interface FooterSettings {
  column1_title: string;
  column2_title: string;
  column3_title: string;
  show_column1: boolean;
  show_column2: boolean;
  show_column3: boolean;
  show_social_links: boolean;
  show_newsletter: boolean;
  newsletter_title: string;
  newsletter_description: string;
  copyright_text: string;
  footer_note: string | null;
}

const SYSTEM_PAGE_TYPES = [
  { type: 'privacy', label: 'Privacy Policy', icon: '🔒' },
  { type: 'terms', label: 'Terms of Service', icon: '📜' },
  { type: 'cookies', label: 'Cookie Policy', icon: '🍪' },
  { type: 'refund', label: 'Refund Policy', icon: '↩️' },
  { type: 'about', label: 'About Us', icon: 'ℹ️' },
  { type: 'contact', label: 'Contact Us', icon: '📧' },
];

const MENU_SECTIONS = [
  { id: 'column1', label: 'Column 1' },
  { id: 'column2', label: 'Column 2' },
  { id: 'column3', label: 'Column 3' },
  { id: 'legal', label: 'Legal Links' },
];

export default function StorePagesPage() {
  const { store } = useOutletContext<OutletContext>();
  const { t } = useTranslation('ecommerce');

  // State
  const [pages, setPages] = useState<StorePage[]>([]);
  const [menuItems, setMenuItems] = useState<FooterMenuItem[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<'pages' | 'footer'>('pages');

  // Page editor state
  const [editingPage, setEditingPage] = useState<StorePage | null>(null);
  const [isCreatingPage, setIsCreatingPage] = useState(false);
  const [newPageType, setNewPageType] = useState<string>('');

  // Menu item editor state
  const [editingMenuItem, setEditingMenuItem] = useState<FooterMenuItem | null>(null);
  const [isCreatingMenuItem, setIsCreatingMenuItem] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load pages
      const pagesResponse = await api.get(`/api/ecommerce/stores/${store.id}/pages`);
      if (pagesResponse.ok) {
        const pagesData = await pagesResponse.json() as { pages: StorePage[] };
        if (pagesData.pages) {
          setPages(pagesData.pages);
        }
      }

      // Load footer settings and menu
      const footerResponse = await api.get(`/api/ecommerce/stores/${store.id}/footer`);
      if (footerResponse.ok) {
        const footerData = await footerResponse.json() as { settings: FooterSettings; menuItems: FooterMenuItem[] };
        if (footerData.settings) {
          setFooterSettings(footerData.settings);
        }
        if (footerData.menuItems) {
          setMenuItems(footerData.menuItems);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [store.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Show success message temporarily
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Page handlers
  const handleCreatePage = async (pageData: Partial<StorePage>) => {
    try {
      setSaving(true);
      await api.post(`/api/ecommerce/stores/${store.id}/pages`, pageData);
      showSuccess('Page created successfully');
      setIsCreatingPage(false);
      setNewPageType('');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create page');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePage = async (pageId: string, pageData: Partial<StorePage>) => {
    try {
      setSaving(true);
      await api.put(`/api/ecommerce/stores/${store.id}/pages/${pageId}`, pageData);
      showSuccess('Page updated successfully');
      setEditingPage(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update page');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!confirm('Are you sure you want to delete this page?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/ecommerce/stores/${store.id}/pages/${pageId}`);
      showSuccess('Page deleted successfully');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete page');
    } finally {
      setSaving(false);
    }
  };

  // Footer settings handlers
  const handleUpdateFooterSettings = async (settings: Partial<FooterSettings>) => {
    try {
      setSaving(true);
      await api.put(`/api/ecommerce/stores/${store.id}/footer`, settings);
      showSuccess('Footer settings updated');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update footer settings');
    } finally {
      setSaving(false);
    }
  };

  // Menu item handlers
  const handleCreateMenuItem = async (itemData: Partial<FooterMenuItem>) => {
    try {
      setSaving(true);
      await api.post(`/api/ecommerce/stores/${store.id}/footer/menu`, itemData);
      showSuccess('Menu item created');
      setIsCreatingMenuItem(false);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create menu item');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMenuItem = async (itemId: string, itemData: Partial<FooterMenuItem>) => {
    try {
      setSaving(true);
      await api.put(`/api/ecommerce/stores/${store.id}/footer/menu/${itemId}`, itemData);
      showSuccess('Menu item updated');
      setEditingMenuItem(null);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update menu item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMenuItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this menu item?')) return;

    try {
      setSaving(true);
      await api.delete(`/api/ecommerce/stores/${store.id}/footer/menu/${itemId}`);
      showSuccess('Menu item deleted');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete menu item');
    } finally {
      setSaving(false);
    }
  };

  const customPages = pages.filter(p => p.page_type === 'custom');
  const canCreateCustomPage = customPages.length < 5;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('pages.title', 'Pages & Footer')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('pages.description', 'Manage your store pages and customize the footer')}
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <span className="text-red-600">Error: {error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <span className="text-green-600">{successMessage}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('pages')}
            className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pages'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('pages.pagesTab', 'Store Pages')}
          </button>
          <button
            onClick={() => setActiveTab('footer')}
            className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'footer'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('pages.footerTab', 'Footer Settings')}
          </button>
        </nav>
      </div>

      {/* Pages Tab */}
      {activeTab === 'pages' && (
        <div className="space-y-6">
          {/* System Pages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('pages.legalPages', 'Legal & Information Pages')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('pages.legalPagesDescription', 'Standard pages like Privacy Policy, Terms of Service, etc.')}
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {SYSTEM_PAGE_TYPES.map(pageType => {
                const existingPage = pages.find(p => p.page_type === pageType.type);
                return (
                  <div key={pageType.type} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{pageType.icon}</span>
                      <div>
                        <h3 className="font-medium text-gray-900">{pageType.label}</h3>
                        {existingPage ? (
                          <p className="text-sm text-gray-500">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800 mr-2">
                              Custom
                            </span>
                            {existingPage.is_published ? 'Published' : 'Draft'} • Last updated:{' '}
                            {new Date(existingPage.updated_at).toLocaleDateString()}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 mr-2">
                              Default
                            </span>
                            Using auto-generated content
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`/s/${store.slug}/${pageType.type}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                      >
                        View
                      </a>
                      {existingPage ? (
                        <>
                          <button
                            onClick={() => setEditingPage(existingPage)}
                            className="px-3 py-1.5 text-sm bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePage(existingPage.id)}
                            className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700"
                            title="Revert to default content"
                          >
                            Reset
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setNewPageType(pageType.type);
                            setIsCreatingPage(true);
                          }}
                          className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                        >
                          Customize
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom Pages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('pages.customPages', 'Custom Pages')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('pages.customPagesDescription', 'Create up to 5 additional custom pages')} ({customPages.length}/5)
                </p>
              </div>
              {canCreateCustomPage && (
                <button
                  onClick={() => {
                    setNewPageType('custom');
                    setIsCreatingPage(true);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
                >
                  + Add Custom Page
                </button>
              )}
            </div>

            {customPages.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-gray-500">No custom pages yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {customPages.map(page => (
                  <div key={page.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{page.title}</h3>
                      <p className="text-sm text-gray-500">
                        /{page.slug} • {page.is_published ? 'Published' : 'Draft'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`/s/${store.slug}/page/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                      >
                        View
                      </a>
                      <button
                        onClick={() => setEditingPage(page)}
                        className="px-3 py-1.5 text-sm bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePage(page.id)}
                        className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Tab */}
      {activeTab === 'footer' && (
        <div className="space-y-6">
          {/* Footer Settings */}
          <FooterSettingsEditor
            settings={footerSettings}
            onSave={handleUpdateFooterSettings}
            saving={saving}
          />

          {/* Footer Menu */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('pages.footerMenu', 'Footer Menu Items')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('pages.footerMenuDescription', 'Add and organize links in your footer')}
                </p>
              </div>
              <button
                onClick={() => setIsCreatingMenuItem(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
              >
                + Add Menu Item
              </button>
            </div>

            {/* Menu Items by Section */}
            {MENU_SECTIONS.map(section => {
              const sectionItems = menuItems.filter(item => item.menu_section === section.id);
              return (
                <div key={section.id} className="border-b border-gray-100 last:border-0">
                  <div className="px-6 py-3 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-700">{section.label}</h3>
                  </div>
                  {sectionItems.length === 0 ? (
                    <div className="px-6 py-4 text-sm text-gray-400">No items in this section</div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {sectionItems.map(item => (
                        <div key={item.id} className="px-6 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${item.is_visible ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{item.label}</span>
                              <span className="text-xs text-gray-400 ml-2">
                                ({item.link_type === 'external_url' ? 'External' : item.link_type === 'store_page' ? item.store_page_type : 'Page'})
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingMenuItem(item)}
                              className="text-sm text-teal-600 hover:text-teal-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteMenuItem(item.id)}
                              className="text-sm text-red-600 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Page Editor Modal */}
      {(editingPage || isCreatingPage) && (
        <PageEditorModal
          page={editingPage}
          pageType={newPageType}
          storeName={store.name}
          onSave={editingPage ? (data) => handleUpdatePage(editingPage.id, data) : handleCreatePage}
          onClose={() => {
            setEditingPage(null);
            setIsCreatingPage(false);
            setNewPageType('');
          }}
          saving={saving}
        />
      )}

      {/* Menu Item Editor Modal */}
      {(editingMenuItem || isCreatingMenuItem) && (
        <MenuItemEditorModal
          item={editingMenuItem}
          pages={pages}
          onSave={editingMenuItem ? (data) => handleUpdateMenuItem(editingMenuItem.id, data) : handleCreateMenuItem}
          onClose={() => {
            setEditingMenuItem(null);
            setIsCreatingMenuItem(false);
          }}
          saving={saving}
        />
      )}
    </div>
  );
}

// Footer Settings Editor Component
function FooterSettingsEditor({
  settings,
  onSave,
  saving,
}: {
  settings: FooterSettings | null;
  onSave: (settings: Partial<FooterSettings>) => void;
  saving: boolean;
}) {
  const [localSettings, setLocalSettings] = useState<Partial<FooterSettings>>(settings || {});

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Footer Settings</h2>
      </div>

      <div className="p-6 space-y-6">
        {/* Column Titles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column 1 Title</label>
            <input
              type="text"
              value={localSettings.column1_title || ''}
              onChange={(e) => setLocalSettings({ ...localSettings, column1_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column 2 Title</label>
            <input
              type="text"
              value={localSettings.column2_title || ''}
              onChange={(e) => setLocalSettings({ ...localSettings, column2_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Column 3 Title</label>
            <input
              type="text"
              value={localSettings.column3_title || ''}
              onChange={(e) => setLocalSettings({ ...localSettings, column3_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Visibility Toggles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { key: 'show_column1', label: 'Show Column 1' },
            { key: 'show_column2', label: 'Show Column 2' },
            { key: 'show_column3', label: 'Show Column 3' },
            { key: 'show_social_links', label: 'Show Social Links' },
            { key: 'show_newsletter', label: 'Show Newsletter' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings[key as keyof FooterSettings] as boolean || false}
                onChange={(e) => setLocalSettings({ ...localSettings, [key]: e.target.checked })}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>

        {/* Copyright Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Copyright Text
            <span className="font-normal text-gray-400 ml-2">Use {'{year}'} and {'{store_name}'} as placeholders</span>
          </label>
          <input
            type="text"
            value={localSettings.copyright_text || ''}
            onChange={(e) => setLocalSettings({ ...localSettings, copyright_text: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="© {year} {store_name}. All rights reserved."
          />
        </div>

        {/* Footer Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Footer Note (optional)</label>
          <textarea
            value={localSettings.footer_note || ''}
            onChange={(e) => setLocalSettings({ ...localSettings, footer_note: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            rows={2}
            placeholder="Additional text to display in the footer"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={() => onSave(localSettings)}
            disabled={saving}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Page Editor Modal
function PageEditorModal({
  page,
  pageType,
  storeName,
  onSave,
  onClose,
  saving,
}: {
  page: StorePage | null;
  pageType: string;
  storeName: string;
  onSave: (data: Partial<StorePage>) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const isNew = !page;
  const type = page?.page_type || pageType;
  const isCustom = type === 'custom';

  const [formData, setFormData] = useState({
    page_type: type,
    slug: page?.slug || '',
    title: page?.title || getDefaultTitle(type, storeName),
    content: page?.content || getDefaultContent(type, storeName),
    meta_description: page?.meta_description || '',
    meta_keywords: page?.meta_keywords || '',
    is_published: page?.is_published ?? true,
  });

  function getDefaultTitle(pageType: string, storeName: string): string {
    const titles: Record<string, string> = {
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Policy',
      refund: 'Refund Policy',
      about: `About ${storeName}`,
      contact: 'Contact Us',
    };
    return titles[pageType] || '';
  }

  function getDefaultContent(pageType: string, storeName: string): string {
    const templates: Record<string, string> = {
      privacy: `# Privacy Policy

**Last updated: ${new Date().toLocaleDateString()}**

At ${storeName}, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.

## Information We Collect

We collect information that you provide directly to us, such as when you create an account, make a purchase, or contact us for support.

### Personal Information
- Name and contact information
- Payment information
- Shipping address
- Order history

## How We Use Your Information

We use the information we collect to:
- Process your orders and payments
- Communicate with you about your orders
- Send promotional communications (with your consent)
- Improve our website and services

## Contact Us

If you have questions about this Privacy Policy, please contact us.`,

      terms: `# Terms of Service

**Last updated: ${new Date().toLocaleDateString()}**

Welcome to ${storeName}. By accessing or using our website, you agree to be bound by these Terms of Service.

## Use of Our Service

You agree to use our service only for lawful purposes and in accordance with these Terms.

## Products and Pricing

All prices are subject to change without notice. We reserve the right to modify or discontinue products at any time.

## Payment Terms

Payment is due at the time of purchase. We accept various payment methods as indicated on our website.

## Shipping and Delivery

Shipping times and costs vary depending on location and shipping method selected.

## Returns and Refunds

Please refer to our Refund Policy for information about returns and refunds.

## Contact Us

If you have questions about these Terms, please contact us.`,

      cookies: `# Cookie Policy

**Last updated: ${new Date().toLocaleDateString()}**

This Cookie Policy explains how ${storeName} uses cookies and similar technologies.

## What Are Cookies

Cookies are small text files stored on your device when you visit our website.

## Types of Cookies We Use

### Necessary Cookies
These cookies are essential for the website to function properly.

### Analytics Cookies
These help us understand how visitors interact with our website.

### Marketing Cookies
These are used to deliver relevant advertisements.

### Preference Cookies
These remember your preferences and settings.

## Managing Cookies

You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.

## Contact Us

If you have questions about our use of cookies, please contact us.`,

      refund: `# Refund Policy

**Last updated: ${new Date().toLocaleDateString()}**

At ${storeName}, we want you to be completely satisfied with your purchase.

## Returns

We accept returns within 30 days of delivery for most items in their original condition.

## How to Return

1. Contact our customer service team
2. Receive a return authorization
3. Ship the item back to us
4. Receive your refund

## Refund Process

Refunds will be processed within 5-10 business days after we receive your return.

## Exceptions

Some items may not be eligible for return, including:
- Personalized items
- Items marked as final sale
- Items that have been used or damaged

## Contact Us

For questions about returns or refunds, please contact us.`,

      about: `# About ${storeName}

Welcome to ${storeName}!

## Our Story

Tell your customers about your business, your mission, and what makes you unique.

## Our Values

- Quality products
- Excellent customer service
- Fast shipping

## Our Team

Introduce your team members here.

## Contact Us

We'd love to hear from you! Contact us anytime.`,

      contact: `# Contact Us

We're here to help! Reach out to us using any of the methods below.

## Customer Service

For questions about orders, returns, or products:
- Email: support@example.com
- Phone: (555) 123-4567
- Hours: Monday-Friday, 9am-5pm

## Business Inquiries

For partnership or business inquiries:
- Email: business@example.com

## Visit Us

[Your address here]

## Send Us a Message

Feel free to reach out with any questions or feedback.`,
    };
    return templates[pageType] || '';
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isNew ? 'Create Page' : 'Edit Page'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Slug (for custom pages only) */}
          {isCustom && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL Slug *
              </label>
              <div className="flex items-center">
                <span className="text-gray-400 text-sm mr-2">/page/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="my-custom-page"
                  required={isCustom}
                />
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Page Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content * <span className="font-normal text-gray-400">(Markdown supported)</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono text-sm"
              rows={15}
              required
            />
          </div>

          {/* SEO Fields */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows={2}
                  maxLength={160}
                  placeholder="Brief description for search engines (max 160 characters)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Keywords</label>
                <input
                  type="text"
                  value={formData.meta_keywords}
                  onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </div>
          </div>

          {/* Published Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <label htmlFor="is_published" className="text-sm text-gray-700">
              Publish this page (make it visible to visitors)
            </label>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isNew ? 'Create Page' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Menu Item Editor Modal
function MenuItemEditorModal({
  item,
  pages,
  onSave,
  onClose,
  saving,
}: {
  item: FooterMenuItem | null;
  pages: StorePage[];
  onSave: (data: Partial<FooterMenuItem>) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const isNew = !item;

  const [formData, setFormData] = useState({
    menu_section: item?.menu_section || 'column1',
    label: item?.label || '',
    link_type: item?.link_type || 'external_url',
    url: item?.url || '',
    page_id: item?.page_id || '',
    store_page_type: item?.store_page_type || '',
    open_new_tab: item?.open_new_tab ?? false,
    is_visible: item?.is_visible ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isNew ? 'Add Menu Item' : 'Edit Menu Item'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Section</label>
            <select
              value={formData.menu_section}
              onChange={(e) => setFormData({ ...formData, menu_section: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {MENU_SECTIONS.map(section => (
                <option key={section.id} value={section.id}>{section.label}</option>
              ))}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              required
            />
          </div>

          {/* Link Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
            <select
              value={formData.link_type}
              onChange={(e) => setFormData({ ...formData, link_type: e.target.value, url: '', page_id: '', store_page_type: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="external_url">External URL</option>
              <option value="store_page">Built-in Page (Privacy, Terms, etc.)</option>
              <option value="internal_page">Custom Page</option>
            </select>
          </div>

          {/* URL (for external) */}
          {formData.link_type === 'external_url' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="https://example.com"
                required
              />
            </div>
          )}

          {/* Store Page Type (for built-in pages) */}
          {formData.link_type === 'store_page' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page *</label>
              <select
                value={formData.store_page_type}
                onChange={(e) => setFormData({ ...formData, store_page_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                required
              >
                <option value="">Select a page...</option>
                {SYSTEM_PAGE_TYPES.map(pt => (
                  <option key={pt.type} value={pt.type}>{pt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Page ID (for custom pages) */}
          {formData.link_type === 'internal_page' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page *</label>
              <select
                value={formData.page_id}
                onChange={(e) => setFormData({ ...formData, page_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                required
              >
                <option value="">Select a page...</option>
                {pages.map(page => (
                  <option key={page.id} value={page.id}>{page.title}</option>
                ))}
              </select>
            </div>
          )}

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.open_new_tab}
                onChange={(e) => setFormData({ ...formData, open_new_tab: e.target.checked })}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Open in new tab</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_visible}
                onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isNew ? 'Add Item' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
